import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { PatientRefIntervenor } from "@/Common/Api/Patient/Dto/PatientRefIntervenorWithDetails";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { EmployeeTitle } from "@/Common/Models/Employee/Enums/EmployeeTitle";
import { IPatientFileAllowlistChanges } from "@/Common/Models/Patient/IPatientFileAllowlist";
import { Patient as MyleModelsPatientPatient } from "@/Common/Models/Patient/Patient";
import { IPatientContactsModel } from "@/Common/Models/Patient/UI/Profile/IPatientContactsModel";
import { IPatientDemographicsModel } from "@/Common/Models/Patient/UI/Profile/IPatientDemographicsModel";
import { IPatientIdentifiersModel } from "@/Common/Models/Patient/UI/Profile/IPatientIdentifiersModel";
import { IPatientOtherInformationModel } from "@/Common/Models/Patient/UI/Profile/IPatientOtherInformationModel";
import { IPatientRelationshipsModel } from "@/Common/Models/Patient/UI/Profile/IPatientRelationshipsModel";
import { RAMQCaracteristiqueSante } from "@/Common/Models/RAMQ/RAMQCaracteristiqueSante";
import { RAMQCurrentUser } from "@/Common/Models/RAMQ/RAMQCurrentUser";
import { RAMQDoctor } from "@/Common/Models/RAMQ/RAMQDoctor";
import { RAMQLieuDeSuivi } from "@/Common/Models/RAMQ/RAMQLieuDeSuivi";
import { RAMQPathology } from "@/Common/Models/RAMQ/RAMQPathology";
import { RAMQPatient } from "@/Common/Models/RAMQ/RAMQPatient";
import { RAMQResident } from "@/Common/Models/RAMQ/RAMQPatientRegistration";
import { RAMQRepresentant } from "@/Common/Models/RAMQ/RAMQRepresentant";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { BaseAction, IRequestExecutionContext, IResponseExecutionContext } from "@/Common/Stores/Actions/BaseAction";
import { ExportableFile } from "@/ExportableFile/Models/ExportableFile";
import { IPatientUIModel } from "@/Patient/Models/UI/IPatientUIModel";
import { PatientStore } from "@/Patient/Stores/PatientStore";

class PatientProfileActions extends BaseAction {
    constructor(onBeforeExecute?: IRequestExecutionContext, onBeforeHandleResponse?: IResponseExecutionContext) {
        super(onBeforeExecute, onBeforeHandleResponse);
    }

    public loadPatient(patientContactId: string, then: () => void = null, subOperation: SubServerOperations.Patient = SubServerOperations.Patient.LoadPatientAndRedirect): void {
        if (patientContactId && patientContactId.length >= 0) {
            let requestData = {
                IndividualId: patientContactId,
                IncludeRelatedRecalls: true,
                IncludeRelationships: true
            };
            return this.dispatch(ServerOperations.Server.Myle_LoadPatient, requestData, then, null, subOperation);
        }
    }

    public loadProtectedPatient(patientContactId: string, then: () => void = null, subOperation?: SubServerOperations.Patient): void {
        if (patientContactId && patientContactId.length >= 0) {
            let requestData = {
                IndividualId: patientContactId,
                IncludeRelatedRecalls: true,
                IncludeRelationships: true
            };
            return this.dispatch(ServerOperations.Server.Myle_LoadProtectedPatient, requestData, then, null, subOperation);
        }
    }

    public loadRamqInfo(patientModel: MyleModelsPatientPatient): void {
        if (sessionStore.clientConfiguration?.HasMedfarRAMQ !== true && cacheStore.ClinicSettings.EnableRamqGL6Service !== true)
            return;

        let requestData = {
            Patient: {
                IndividualID: patientModel.IndividualResponse.Contact_id
            },
            CurrentUser: sessionStore.getCurrentEmployee().getRamqCredentials()
        };

        this.Execute(ServerOperations.Server.RAMQ_CheckSubscription, requestData);
    }

    public saveMemo(newMemo: string, contactId: string, contactFacetId: string, isDeletion: boolean = false): void {
        let requestData = {
            PatientContactId: contactId,
            PatientContactFacetId: contactFacetId,
            MemoText: newMemo,
            IsDeletion: isDeletion
        };

        return this.Execute(ServerOperations.Server.Myle_Patient_Memo_Save, requestData);
    }

    public saveProfilePic(path: string, patientContactId: string): void {
        let requestData = {
            ContactId: patientContactId,
            ImagePath: path
        };

        this.Execute(ServerOperations.Server.Myle_SaveProfileImage, requestData, SubServerOperations.Profile.SaveProfileImage);
    }

    public savePatientDemographics(demographicsModel: IPatientDemographicsModel): void {
        if (!demographicsModel)
            return;

        let requestData = {
            PatientDemographicsDTO: (<Immutable.Map<any, any>>demographicsModel).toObject()
        };

        if (demographicsModel.get("DateOfBirth")) {
            requestData.PatientDemographicsDTO.DateOfBirth = demographicsModel.get("DateOfBirth")?.toDateOnly();
        }

        return this.Execute(ServerOperations.Server.Myle_Patient_Demographics_Update, requestData);
    }

    public savePatientIdentifiers(idModels: IPatientIdentifiersModel): void {
        if (!idModels)
            return;

        let requestData = {
            PatientIdentifiersDTO: (<Immutable.Map<any, any>>idModels).toObject()
        };

        return this.Execute(ServerOperations.Server.Myle_Patient_Identifiers_Update, requestData);
    }

    public savePatientContacts(contactsModel: IPatientContactsModel): void {
        if (!contactsModel)
            return;

        let requestData = {
            PatientContactsDTO: contactsModel
        };

        return this.Execute(ServerOperations.Server.Myle_Patient_Contacts_Update, requestData);
    }

    public savePatientOtherInfo(uiModel: IPatientUIModel, otherInfoModel: IPatientOtherInformationModel): void {
        if (!otherInfoModel)
            return;

        const preferredPharmacy = otherInfoModel.get("PreferredPharmacy");
        const directoryPharmacyId = preferredPharmacy?.DirectoryPharmacyId;
        const pharmacyInfo = preferredPharmacy?.getPharmacyInfo();

        let requestData = {
            PatientOtherInfoDTO: {
                PatientContactId: uiModel.get("PatientContactId"),
                PatientContactFacetId: uiModel.get("PatientContactFacetId"),
                DirectoryPharmacyId: directoryPharmacyId,
                PharmacyName: pharmacyInfo?.Name,
                PharmacyPhone: pharmacyInfo?.Phone,
                PharmacyFax: pharmacyInfo?.Fax,
                PharmacyBanner: cacheStore.ClinicSettings.EnableContactDirectory
                    ? pharmacyInfo?.Banner
                    : otherInfoModel.get("SearchString"),
                PharmacyIsMylePharmacy: pharmacyInfo?.IsMylePharmacy,
                SelectedInsuranceDetailId: !(otherInfoModel.get("SelectedInsuranceDetailId") == null && otherInfoModel.get("SelectedInsuranceDetailId").length > 0)
                    ? otherInfoModel.get("SelectedInsuranceDetailId")
                    : null,
                PharmacyAddress: pharmacyInfo?.Address,
                InsuranceCompanyName: otherInfoModel.get("InsuranceCompanyName"),
                InsurancePolicyNo: otherInfoModel.get("InsurancePolicyNo")
            }
        };

        return this.Execute(ServerOperations.Server.Myle_Patient_OtherInfo_Update, requestData);
    }

    public savePatientClinicians(patientContactFacedId: string, patientRefIntervenors: PatientRefIntervenor[], patientFileAllowlist?: IPatientFileAllowlistChanges): void {
        const requestData = {
            PatientContactFacetId: patientContactFacedId,
            PatientRefIntervenors: patientRefIntervenors,
            PatientFileAllowlistChanges: patientFileAllowlist
        };

        return this.Execute(ServerOperations.Server.Myle_Patient_Clinicians_Update, requestData);
    }

    public savePatientRelationships(uiModel: IPatientUIModel, relationshipsModel: IPatientRelationshipsModel, stayInEditMode: boolean = false): void {
        return this.Execute(ServerOperations.Server.Myle_Patient_Relationships_Update, {
            PatientRelationshipsDTO: {
                PatientContactId: uiModel.get("PatientContactId"),
                PatientContactFacetId: uiModel.get("PatientContactFacetId"),
                Relationships: relationshipsModel.get("Relationships"),
                DeleteRelationshipIds: relationshipsModel.get("DeleteRelationshipIds")
            },
            StayInEditMode: stayInEditMode
        });
    }

    // -------------------------------------------------------------------------------------------------------------------------
    // - enrolment -------------------------------------------------------------------------------------------------------------
    // -------------------------------------------------------------------------------------------------------------------------
    public enrollmentTransmit(patientId: string, store: PatientStore, includeYesAnswer: boolean = false, needPrinting: boolean = false): void {
        let patientModel = store.PatientModels[patientId];
        let patientIdentifiersModel = store.PatientProfileIdentifiersModels[patientId];
        let enrollmentModel = store.PatientProfileEnrollmentModels[patientId];
        let healthId = patientIdentifiersModel.get("HealthCards").toArray();
        let mainHealthCard = healthId[0];

        let ramqPatient = new RAMQPatient();
        ramqPatient.NAM = mainHealthCard != null ? mainHealthCard.Number : null;
        ramqPatient.DDN = patientModel.IndividualResponse.DateOfBirth;
        if (ramqPatient.DDN == null && ramqPatient.NAM != null)
            ramqPatient.DDN = PatientHelper.getDateOfBirthFromNAM(ramqPatient.NAM, cacheStore.FindLookupByLookupCode("CanadaState_QC").LookupID);
        ramqPatient.DDN_string = ramqPatient.DDN != null ? ramqPatient.DDN.toString() : "";
        ramqPatient.FirstName = patientModel.IndividualResponse.FirstName;
        ramqPatient.LastName = patientModel.IndividualResponse.LastName;
        ramqPatient.IndividualID = patientModel.IndividualResponse.Contact_id;

        try {
            if (patientModel.IndividualResponse.GenderLookupId != null)
                ramqPatient.Sexe = cacheStore.GetLookupById(patientModel.IndividualResponse.GenderLookupId).en.substr(0, 1);
            else if (ramqPatient.NAM != null) {
                if (ramqPatient.NAM.length >= 7) {
                    if (parseInt(ramqPatient.NAM.substr(7, 1)) >= 5)
                        ramqPatient.Sexe = "F";
                    if (parseInt(ramqPatient.NAM.substr(7, 1)) < 5)
                        ramqPatient.Sexe = "M";
                }
            }
        }
        catch (err) {
            console.log("cannot extrapolate patient gender");
        }

        ramqPatient.InscriptionCodePostal = (patientModel.Addresses && patientModel.Addresses.length > 0) ? patientModel.Addresses[0].ZipCode : null;
        ramqPatient.InscriptionRue = (patientModel.Addresses && patientModel.Addresses.length > 0) ? patientModel.Addresses[0].StreetAddress : null;
        ramqPatient.InscriptionVille = (patientModel.Addresses && patientModel.Addresses.length > 0) ? patientModel.Addresses[0].City : null;
        ramqPatient.FileNumber = patientModel.PatientResponse.Identifier01;

        let ramqCurrentUser = new RAMQCurrentUser();
        ramqCurrentUser.AIPu = sessionStore.getCurrentEmployee().getActiveSite().Model.AipLogin;
        ramqCurrentUser.CElogin = sessionStore.getCurrentEmployee().Model.CeLoginRamq;

        let ramqRep = new RAMQRepresentant();
        ramqRep.FirstName = enrollmentModel.get("RepFirstName");
        ramqRep.LastName = enrollmentModel.get("RepLastName");
        ramqRep.NAM = enrollmentModel.get("RepNam");
        ramqRep.Type = enrollmentModel.get("SelectedRepTypeId");

        let ramqResident = new RAMQResident();

        if (enrollmentModel.get("SelectedResidentId")) {
            let res = cacheStore.GetEmployeeByContactFacetId(enrollmentModel.get("SelectedResidentId"));

            if (Employee.getIssuingOrgRule(res).Title === EmployeeTitle.NursePractitioner) {
                ramqResident.Class = "8";
                ramqResident.PermitNumber = res.RamqNumber;
            }
            else {
                ramqResident.Class = "5";
                ramqResident.PermitNumber = res.FirstLicenceNumber;
            }
            ramqResident.Name = res.FirstName + " " + res.LastName;
        }

        let ramqDoctor = new RAMQDoctor();

        if (enrollmentModel.get("SelectedDispensateurId")) {
            const employee = cacheStore.GetEmployeeByContactFacetId(enrollmentModel.get("SelectedDispensateurId"));

            ramqDoctor.Class = enrollmentModel.get("DispensateurClass");
            ramqDoctor.NoGMF = enrollmentModel.get("NoGmf");
            ramqDoctor.Licence = enrollmentModel.get("DispensateurNumber");
            ramqDoctor.FirstName = employee.FirstName;
            ramqDoctor.LastName = employee.LastName;
        }

        let ramqPatho = new RAMQPathology();
        ramqPatho.PathoList = [];
        ramqPatho.ErrorMessages = [];
        if (enrollmentModel.get("CaracteristiqueSante1")) {
            let c1 = new RAMQCaracteristiqueSante();
            c1.CodeSante = parseInt(enrollmentModel.get("CaracteristiqueSante1"));
            c1.Date = MyleHelper.GetStandardizedDateString(enrollmentModel.get("CaracteristiqueSante1Date"));
            ramqPatho.PathoList.push(c1);
        }

        if (enrollmentModel.get("CaracteristiqueSante2")) {
            let c2 = new RAMQCaracteristiqueSante();
            c2.CodeSante = parseInt(enrollmentModel.get("CaracteristiqueSante2"));
            c2.Date = MyleHelper.GetStandardizedDateString(enrollmentModel.get("CaracteristiqueSante2Date"));
            ramqPatho.PathoList.push(c2);
        }

        if (enrollmentModel.get("CaracteristiqueSante3")) {
            let c3 = new RAMQCaracteristiqueSante();
            c3.CodeSante = parseInt(enrollmentModel.get("CaracteristiqueSante3"));
            c3.Date = MyleHelper.GetStandardizedDateString(enrollmentModel.get("CaracteristiqueSante3Date"));
            ramqPatho.PathoList.push(c3);
        }

        let ramqLieuSuivi = new RAMQLieuDeSuivi();
        if (enrollmentModel.get("SelectedLieuSuiviId") != null) {
            let site = cacheStore.GetSiteById(enrollmentModel.get("SelectedLieuSuiviId"));

            ramqLieuSuivi.CodeGMF = parseInt(enrollmentModel.get("NoGmf"));
            ramqLieuSuivi.CodeLieu = parseInt(site.Model.ClinicalCode);
            ramqLieuSuivi.Date = MyleHelper.GetStandardizedDateString(enrollmentModel.get("DateDebutLieuSuivi"));
        }

        let requestData = {
            RAMQPatient: ramqPatient,
            RAMQCurrentUser: ramqCurrentUser,
            RAMQRepresentant: ramqRep,
            RAMQResident: ramqResident,
            RAMQDoctor: ramqDoctor,
            RAMQPathology: ramqPatho,
            RAMQLieuDeSuivi: ramqLieuSuivi,
            RegistrationDate: MyleHelper.GetStandardizedDateString(enrollmentModel.get("EnrollmentEffectiveDate")),
            EnrollmentSiteId: enrollmentModel.get("SelectedLieuSuiviId"),
            QuestionCode: -10, // set to -10 initialy... not an answer to any ramq questions
            Answer: "",
            NeedPrinting: needPrinting
        };

        if (includeYesAnswer) {
            requestData.QuestionCode = enrollmentModel.get("RAMQQuestionCode");
            requestData.Answer = "O";
        }

        this.Execute(ServerOperations.Server.RAMQ_Inscription, requestData);
    }

    public enrollmentTransmitDate(patientId: string, store: PatientStore, includeYesAnswer: boolean = false): void {
        let patientModel = store.PatientModels[patientId];
        let patientIdentifiersModel = store.PatientProfileIdentifiersModels[patientId];
        let enrollmentModel = store.PatientProfileEnrollmentModels[patientId];

        let healthId = patientIdentifiersModel.get("HealthCards").toArray();
        let mainHealthCard = healthId[0];

        let ramqPatient = new RAMQPatient();
        ramqPatient.NAM = mainHealthCard != null ? mainHealthCard.Number : null;

        ramqPatient.DDN = patientModel.IndividualResponse.DateOfBirth;

        if (ramqPatient.DDN == null && ramqPatient.NAM != null)
            ramqPatient.DDN = PatientHelper.getDateOfBirthFromNAM(ramqPatient.NAM, cacheStore.FindLookupByLookupCode("CanadaState_QC").LookupID);

        ramqPatient.DDN_string = ramqPatient.DDN != null ? ramqPatient.DDN.toString() : "";

        ramqPatient.FirstName = patientModel.IndividualResponse.FirstName;
        ramqPatient.LastName = patientModel.IndividualResponse.LastName;
        ramqPatient.IndividualID = patientModel.IndividualResponse.Contact_id;
        ramqPatient.Sexe = cacheStore.GetLookupById(patientModel.IndividualResponse.GenderLookupId).en.substr(0, 1);
        ramqPatient.InscriptionCodePostal = (patientModel.Addresses && patientModel.Addresses.length > 0) ? patientModel.Addresses[0].ZipCode : null;
        ramqPatient.InscriptionRue = (patientModel.Addresses && patientModel.Addresses.length > 0) ? patientModel.Addresses[0].StreetAddress : null;
        ramqPatient.InscriptionVille = (patientModel.Addresses && patientModel.Addresses.length > 0) ? patientModel.Addresses[0].City : null;
        ramqPatient.FileNumber = patientModel.PatientResponse.Identifier01;

        let ramqCurrentUser = new RAMQCurrentUser();
        ramqCurrentUser.AIPu = sessionStore.getCurrentEmployee().getActiveSite().Model.AipLogin;
        ramqCurrentUser.CElogin = sessionStore.getCurrentEmployee().Model.CeLoginRamq;

        let requestData = {
            RAMQPatient: ramqPatient,
            RAMQCurrentUser: ramqCurrentUser,
            NewEffectiveDate: MyleHelper.GetStandardizedDateString(enrollmentModel.get("EnrollmentEffectiveDate")),
            QuestionCode: -10,
            Answer: ""
        };

        if (includeYesAnswer) {
            requestData.QuestionCode = enrollmentModel.get("RAMQQuestionCode");
            requestData.Answer = "O";
        }

        this.Execute(ServerOperations.Server.RAMQ_ChangeEffectiveDate, requestData);
    }

    public enrollmentTransmitEditResident(patientId: string, store: PatientStore, includeYesAnswer: boolean = false): void {
        let patientModel = store.PatientModels[patientId];
        let patientIdentifiersModel = store.PatientProfileIdentifiersModels[patientId];
        let enrollmentModel = store.PatientProfileEnrollmentModels[patientId];
        let healthId = patientIdentifiersModel.get("HealthCards").toArray();
        let mainHealthCard = healthId[0];
        let ramqPatient = new RAMQPatient();

        ramqPatient.NAM = mainHealthCard != null ? mainHealthCard.Number : null;
        ramqPatient.DDN = patientModel.IndividualResponse.DateOfBirth;
        ramqPatient.DDN_string = patientModel.IndividualResponse.DateOfBirth.toString();
        ramqPatient.FirstName = patientModel.IndividualResponse.FirstName;
        ramqPatient.LastName = patientModel.IndividualResponse.LastName;
        ramqPatient.IndividualID = patientModel.IndividualResponse.Contact_id;
        ramqPatient.Sexe = cacheStore.GetLookupById(patientModel.IndividualResponse.GenderLookupId).en.substr(0, 1);
        ramqPatient.InscriptionCodePostal = (patientModel.Addresses && patientModel.Addresses.length > 0) ? patientModel.Addresses[0].ZipCode : null;
        ramqPatient.InscriptionRue = (patientModel.Addresses && patientModel.Addresses.length > 0) ? patientModel.Addresses[0].StreetAddress : null;
        ramqPatient.InscriptionVille = (patientModel.Addresses && patientModel.Addresses.length > 0) ? patientModel.Addresses[0].City : null;
        ramqPatient.FileNumber = patientModel.PatientResponse.Identifier01;

        let ramqCurrentUser = new RAMQCurrentUser();
        ramqCurrentUser.AIPu = sessionStore.getCurrentEmployee().getActiveSite().Model.AipLogin;
        ramqCurrentUser.CElogin = sessionStore.getCurrentEmployee().Model.CeLoginRamq;

        let ramqResident = new RAMQResident();
        if (enrollmentModel.get("SelectedResidentId")) {
            const employee = cacheStore.GetEmployeeByContactFacetId(enrollmentModel.get("SelectedResidentId"));

            if (Employee.getIssuingOrgRule(employee).Title === EmployeeTitle.NursePractitioner) {
                ramqResident.Class = "8";
                ramqResident.PermitNumber = employee.RamqNumber;
            }
            else {
                ramqResident.Class = "5";
                ramqResident.PermitNumber = employee.FirstLicenceNumber;
            }
            ramqResident.Name = employee.FirstName + " " + employee.LastName;
        }

        let requestData = {
            RAMQPatient: ramqPatient,
            RAMQCurrentUser: ramqCurrentUser,
            RAMQResident: ramqResident,
            QuestionCode: -10,
            Answer: ""
        };

        if (includeYesAnswer) {
            requestData.QuestionCode = enrollmentModel.get("RAMQQuestionCode");
            requestData.Answer = "O";
        }

        this.Execute(ServerOperations.Server.RAMQ_EditResident, requestData);
    }

    public enrollmentTransmitDeleteResident(patientId: string, store: PatientStore, includeYesAnswer: boolean = false): void {
        let patientModel = store.PatientModels[patientId];
        let patientIdentifiersModel = store.PatientProfileIdentifiersModels[patientId];
        let enrollmentModel = store.PatientProfileEnrollmentModels[patientId];

        let healthId = patientIdentifiersModel.get("HealthCards").toArray();
        let mainHealthCard = healthId[0];

        let ramqPatient = new RAMQPatient();
        ramqPatient.NAM = mainHealthCard != null ? mainHealthCard.Number : null;
        ramqPatient.DDN = patientModel.IndividualResponse.DateOfBirth;
        ramqPatient.DDN_string = patientModel.IndividualResponse.DateOfBirth.toString();
        ramqPatient.FirstName = patientModel.IndividualResponse.FirstName;
        ramqPatient.LastName = patientModel.IndividualResponse.LastName;
        ramqPatient.IndividualID = patientModel.IndividualResponse.Contact_id;
        ramqPatient.Sexe = cacheStore.GetLookupById(patientModel.IndividualResponse.GenderLookupId).en.substr(0, 1);
        ramqPatient.InscriptionCodePostal = (patientModel.Addresses && patientModel.Addresses.length > 0) ? patientModel.Addresses[0].ZipCode : null;
        ramqPatient.InscriptionRue = (patientModel.Addresses && patientModel.Addresses.length > 0) ? patientModel.Addresses[0].StreetAddress : null;
        ramqPatient.InscriptionVille = (patientModel.Addresses && patientModel.Addresses.length > 0) ? patientModel.Addresses[0].City : null;
        ramqPatient.FileNumber = patientModel.PatientResponse.Identifier01;

        let ramqCurrentUser = new RAMQCurrentUser();
        ramqCurrentUser.AIPu = sessionStore.getCurrentEmployee().getActiveSite().Model.AipLogin;
        ramqCurrentUser.CElogin = sessionStore.getCurrentEmployee().Model.CeLoginRamq;

        let requestData = {
            RAMQPatient: ramqPatient,
            RAMQCurrentUser: ramqCurrentUser,
            QuestionCode: -10,
            Answer: ""
        };

        if (includeYesAnswer) {
            requestData.QuestionCode = enrollmentModel.get("RAMQQuestionCode");
            requestData.Answer = "O";
        }

        this.Execute(ServerOperations.Server.RAMQ_DeleteResident, requestData);
    }

    public enrollmentTransmitEditRep(patientId: string, store: PatientStore, includeYesAnswer: boolean = false): void {
        let patientModel = store.PatientModels[patientId];
        let patientIdentifiersModel = store.PatientProfileIdentifiersModels[patientId];
        let enrollmentModel = store.PatientProfileEnrollmentModels[patientId];

        let healthId = patientIdentifiersModel.get("HealthCards").toArray();
        let mainHealthCard = healthId[0];

        let ramqPatient = new RAMQPatient();
        ramqPatient.NAM = mainHealthCard != null ? mainHealthCard.Number : null;
        ramqPatient.DDN = patientModel.IndividualResponse.DateOfBirth;
        ramqPatient.DDN_string = patientModel.IndividualResponse.DateOfBirth.toString();
        ramqPatient.FirstName = patientModel.IndividualResponse.FirstName;
        ramqPatient.LastName = patientModel.IndividualResponse.LastName;
        ramqPatient.IndividualID = patientModel.IndividualResponse.Contact_id;
        ramqPatient.Sexe = cacheStore.GetLookupById(patientModel.IndividualResponse.GenderLookupId).en.substr(0, 1);
        ramqPatient.InscriptionCodePostal = (patientModel.Addresses && patientModel.Addresses.length > 0) ? patientModel.Addresses[0].ZipCode : null;
        ramqPatient.InscriptionRue = (patientModel.Addresses && patientModel.Addresses.length > 0) ? patientModel.Addresses[0].StreetAddress : null;
        ramqPatient.InscriptionVille = (patientModel.Addresses && patientModel.Addresses.length > 0) ? patientModel.Addresses[0].City : null;
        ramqPatient.FileNumber = patientModel.PatientResponse.Identifier01;

        let ramqCurrentUser = new RAMQCurrentUser();
        ramqCurrentUser.AIPu = sessionStore.getCurrentEmployee().getActiveSite().Model.AipLogin;
        ramqCurrentUser.CElogin = sessionStore.getCurrentEmployee().Model.CeLoginRamq;

        let ramqRep = new RAMQRepresentant();
        ramqRep.FirstName = enrollmentModel.get("RepFirstName");
        ramqRep.LastName = enrollmentModel.get("RepLastName");
        ramqRep.NAM = enrollmentModel.get("RepNam");
        ramqRep.Type = enrollmentModel.get("SelectedRepTypeId");

        let requestData = {
            RAMQPatient: ramqPatient,
            RAMQCurrentUser: ramqCurrentUser,
            RAMQRepresentant: ramqRep,
            QuestionCode: -10,
            Answer: ""
        };

        if (includeYesAnswer) {
            requestData.QuestionCode = enrollmentModel.get("RAMQQuestionCode");
            requestData.Answer = "O";
        }

        this.Execute(ServerOperations.Server.RAMQ_EditRepresentant, requestData);
    }

    public enrollmentTransmitDeleteRep(patientId: string, store: PatientStore, includeYesAnswer: boolean = false): void {
        let patientModel = store.PatientModels[patientId];
        let patientIdentifiersModel = store.PatientProfileIdentifiersModels[patientId];
        let enrollmentModel = store.PatientProfileEnrollmentModels[patientId];

        let healthId = patientIdentifiersModel.get("HealthCards").toArray();
        let mainHealthCard = healthId[0];

        let ramqPatient = new RAMQPatient();
        ramqPatient.NAM = mainHealthCard != null ? mainHealthCard.Number : null;
        ramqPatient.DDN = patientModel.IndividualResponse.DateOfBirth;
        ramqPatient.DDN_string = patientModel.IndividualResponse.DateOfBirth.toString();
        ramqPatient.FirstName = patientModel.IndividualResponse.FirstName;
        ramqPatient.LastName = patientModel.IndividualResponse.LastName;
        ramqPatient.IndividualID = patientModel.IndividualResponse.Contact_id;
        ramqPatient.Sexe = cacheStore.GetLookupById(patientModel.IndividualResponse.GenderLookupId).en.substr(0, 1);
        ramqPatient.InscriptionCodePostal = (patientModel.Addresses && patientModel.Addresses.length > 0) ? patientModel.Addresses[0].ZipCode : null;
        ramqPatient.InscriptionRue = (patientModel.Addresses && patientModel.Addresses.length > 0) ? patientModel.Addresses[0].StreetAddress : null;
        ramqPatient.InscriptionVille = (patientModel.Addresses && patientModel.Addresses.length > 0) ? patientModel.Addresses[0].City : null;
        ramqPatient.FileNumber = patientModel.PatientResponse.Identifier01;

        let ramqCurrentUser = new RAMQCurrentUser();
        ramqCurrentUser.AIPu = sessionStore.getCurrentEmployee().getActiveSite().Model.AipLogin;
        ramqCurrentUser.CElogin = sessionStore.getCurrentEmployee().Model.CeLoginRamq;

        let requestData = {
            RAMQPatient: ramqPatient,
            RAMQCurrentUser: ramqCurrentUser,
            QuestionCode: -10,
            Answer: ""
        };

        if (includeYesAnswer) {
            requestData.QuestionCode = enrollmentModel.get("RAMQQuestionCode");
            requestData.Answer = "O";
        }

        this.Execute(ServerOperations.Server.RAMQ_DeleteRepresentant, requestData);
    }

    public enrollmentTransmiLieuSuivi(patientId: string, store: PatientStore, includeYesAnswer: boolean = false): void {
        let patientModel = store.PatientModels[patientId];
        let patientIdentifiersModel = store.PatientProfileIdentifiersModels[patientId];
        let enrollmentModel = store.PatientProfileEnrollmentModels[patientId];

        let healthId = patientIdentifiersModel.get("HealthCards").toArray();
        let mainHealthCard = healthId[0];

        let ramqPatient = new RAMQPatient();
        ramqPatient.NAM = mainHealthCard != null ? mainHealthCard.Number : null;
        ramqPatient.DDN = patientModel.IndividualResponse.DateOfBirth;
        ramqPatient.DDN_string = patientModel.IndividualResponse.DateOfBirth.toString();
        ramqPatient.FirstName = patientModel.IndividualResponse.FirstName;
        ramqPatient.LastName = patientModel.IndividualResponse.LastName;
        ramqPatient.IndividualID = patientModel.IndividualResponse.Contact_id;
        ramqPatient.Sexe = cacheStore.GetLookupById(patientModel.IndividualResponse.GenderLookupId).en.substr(0, 1);
        ramqPatient.InscriptionCodePostal = (patientModel.Addresses && patientModel.Addresses.length > 0) ? patientModel.Addresses[0].ZipCode : null;
        ramqPatient.InscriptionRue = (patientModel.Addresses && patientModel.Addresses.length > 0) ? patientModel.Addresses[0].StreetAddress : null;
        ramqPatient.InscriptionVille = (patientModel.Addresses && patientModel.Addresses.length > 0) ? patientModel.Addresses[0].City : null;
        ramqPatient.FileNumber = patientModel.PatientResponse.Identifier01;

        let ramqCurrentUser = new RAMQCurrentUser();
        ramqCurrentUser.AIPu = sessionStore.getCurrentEmployee().getActiveSite().Model.AipLogin;
        ramqCurrentUser.CElogin = sessionStore.getCurrentEmployee().Model.CeLoginRamq;

        let ramqRep = new RAMQRepresentant();
        ramqRep.FirstName = enrollmentModel.get("RepFirstName");
        ramqRep.LastName = enrollmentModel.get("RepLastName");
        ramqRep.NAM = enrollmentModel.get("RepNam");
        ramqRep.Type = enrollmentModel.get("SelectedRepTypeId");

        let ramqLieuSuivi = new RAMQLieuDeSuivi();
        if (enrollmentModel.get("SelectedLieuSuiviId")) {
            let site = cacheStore.GetSiteById(enrollmentModel.get("SelectedLieuSuiviId"));
            ramqLieuSuivi.CodeLieu = parseInt(site.Model.ClinicalCode);
            ramqLieuSuivi.CodeGMF = parseInt(cacheStore.GetSiteById(site.Model.ParentSiteId).Model.ClinicalCode);
            ramqLieuSuivi.Date = MyleHelper.GetStandardizedDateString(enrollmentModel.get("DateDebutLieuSuivi"));
        }

        let requestData = {
            RAMQPatient: ramqPatient,
            RAMQCurrentUser: ramqCurrentUser,
            RAMQLieuSuivi: ramqLieuSuivi,
            QuestionCode: -10,
            Answer: "",
            EnrollmentSiteId: enrollmentModel.get("SelectedLieuSuiviId")
        };

        if (includeYesAnswer) {
            requestData.QuestionCode = enrollmentModel.get("RAMQQuestionCode");
            requestData.Answer = "O";
        }

        this.Execute(ServerOperations.Server.RAMQ_EditLocationSite, requestData);
    }

    public enrollmentTransmitCaracteristiqueSante(patientId: string, store: PatientStore, includeYesAnswer: boolean = false): void {
        let patientModel = store.PatientModels[patientId];
        let patientIdentifiersModel = store.PatientProfileIdentifiersModels[patientId];
        let enrollmentModel = store.PatientProfileEnrollmentModels[patientId];

        let healthId = patientIdentifiersModel.get("HealthCards").toArray();
        let mainHealthCard = healthId[0];

        let ramqPatient = new RAMQPatient();
        ramqPatient.NAM = mainHealthCard != null ? mainHealthCard.Number : null;
        ramqPatient.DDN = patientModel.IndividualResponse.DateOfBirth;
        ramqPatient.DDN_string = patientModel.IndividualResponse.DateOfBirth.toString();
        ramqPatient.FirstName = patientModel.IndividualResponse.FirstName;
        ramqPatient.LastName = patientModel.IndividualResponse.LastName;
        ramqPatient.IndividualID = patientModel.IndividualResponse.Contact_id;
        ramqPatient.Sexe = cacheStore.GetLookupById(patientModel.IndividualResponse.GenderLookupId).en.substr(0, 1);
        ramqPatient.InscriptionCodePostal = (patientModel.Addresses && patientModel.Addresses.length > 0) ? patientModel.Addresses[0].ZipCode : null;
        ramqPatient.InscriptionRue = (patientModel.Addresses && patientModel.Addresses.length > 0) ? patientModel.Addresses[0].StreetAddress : null;
        ramqPatient.InscriptionVille = (patientModel.Addresses && patientModel.Addresses.length > 0) ? patientModel.Addresses[0].City : null;
        ramqPatient.FileNumber = patientModel.PatientResponse.Identifier01;

        let ramqCurrentUser = new RAMQCurrentUser();
        ramqCurrentUser.AIPu = sessionStore.getCurrentEmployee().getActiveSite().Model.AipLogin;
        ramqCurrentUser.CElogin = sessionStore.getCurrentEmployee().Model.CeLoginRamq;

        let ramqPatho = new RAMQPathology();
        ramqPatho.PathoList = [];
        ramqPatho.ErrorMessages = [];
        if (enrollmentModel.get("CaracteristiqueSante1")) {
            let c1 = new RAMQCaracteristiqueSante();
            c1.CodeSante = parseInt(enrollmentModel.get("CaracteristiqueSante1"));
            c1.Date = MyleHelper.GetStandardizedDateString(enrollmentModel.get("CaracteristiqueSante1Date"));
            ramqPatho.PathoList.push(c1);
        }

        if (enrollmentModel.get("CaracteristiqueSante2")) {
            let c2 = new RAMQCaracteristiqueSante();
            c2.CodeSante = parseInt(enrollmentModel.get("CaracteristiqueSante2"));
            c2.Date = MyleHelper.GetStandardizedDateString(enrollmentModel.get("CaracteristiqueSante2Date"));
            ramqPatho.PathoList.push(c2);
        }

        if (enrollmentModel.get("CaracteristiqueSante3")) {
            let c3 = new RAMQCaracteristiqueSante();
            c3.CodeSante = parseInt(enrollmentModel.get("CaracteristiqueSante3"));
            c3.Date = MyleHelper.GetStandardizedDateString(enrollmentModel.get("CaracteristiqueSante3Date"));
            ramqPatho.PathoList.push(c3);
        }

        let requestData = {
            RAMQPatient: ramqPatient,
            RAMQCurrentUser: ramqCurrentUser,
            RAMQPathology: ramqPatho,
            QuestionCode: -10,
            Answer: ""
        };

        if (includeYesAnswer) {
            requestData.QuestionCode = enrollmentModel.get("RAMQQuestionCode");
            requestData.Answer = "O";
        }

        this.Execute(ServerOperations.Server.RAMQ_SetRamqPathology, requestData);
    }

    public enrollmentTransmitPatientTermination(patientId: string, store: PatientStore, includeYesAnswer: boolean = false): void {
        let patientModel = store.PatientModels[patientId];
        let patientIdentifiersModel = store.PatientProfileIdentifiersModels[patientId];
        let enrollmentModel = store.PatientProfileEnrollmentModels[patientId];

        let healthId = patientIdentifiersModel.get("HealthCards").toArray();
        let mainHealthCard = healthId[0];

        let ramqPatient = new RAMQPatient();
        ramqPatient.NAM = mainHealthCard != null ? mainHealthCard.Number : null;
        ramqPatient.DDN = patientModel.IndividualResponse.DateOfBirth;
        ramqPatient.DDN_string = patientModel.IndividualResponse.DateOfBirth.toString();
        ramqPatient.FirstName = patientModel.IndividualResponse.FirstName;
        ramqPatient.LastName = patientModel.IndividualResponse.LastName;
        ramqPatient.IndividualID = patientModel.IndividualResponse.Contact_id;
        ramqPatient.Sexe = cacheStore.GetLookupById(patientModel.IndividualResponse.GenderLookupId).en.substr(0, 1);
        ramqPatient.InscriptionCodePostal = (patientModel.Addresses && patientModel.Addresses.length > 0) ? patientModel.Addresses[0].ZipCode : null;
        ramqPatient.InscriptionRue = (patientModel.Addresses && patientModel.Addresses.length > 0) ? patientModel.Addresses[0].StreetAddress : null;
        ramqPatient.InscriptionVille = (patientModel.Addresses && patientModel.Addresses.length > 0) ? patientModel.Addresses[0].City : null;
        ramqPatient.FileNumber = patientModel.PatientResponse.Identifier01;

        let ramqCurrentUser = new RAMQCurrentUser();
        ramqCurrentUser.AIPu = sessionStore.getCurrentEmployee().getActiveSite().Model.AipLogin;
        ramqCurrentUser.CElogin = sessionStore.getCurrentEmployee().Model.CeLoginRamq;

        let ramqRep = new RAMQRepresentant();
        ramqRep.FirstName = enrollmentModel.get("RepFirstName");
        ramqRep.LastName = enrollmentModel.get("RepLastName");
        ramqRep.NAM = enrollmentModel.get("RepNam");
        ramqRep.Type = enrollmentModel.get("SelectedRepTypeId");

        let requestData = {
            RAMQPatient: ramqPatient,
            RAMQCurrentUser: ramqCurrentUser,
            CessationType: enrollmentModel.get("SelectedTerminationId"),
            QuestionCode: -10,
            Answer: ""
        };

        if (includeYesAnswer) {
            requestData.QuestionCode = enrollmentModel.get("RAMQQuestionCode");
            requestData.Answer = "O";
        }

        this.Execute(ServerOperations.Server.RAMQ_DeInscription, requestData);
    }

    public generateRamqForm(patientModel: MyleModelsPatientPatient, isEmptyForm: boolean): void {
        let preferredAddress = patientModel.Addresses.filter(c => c.IsPreferred).length > 0
            ? patientModel.Addresses.filter(c => c.IsPreferred).firstOrDefault()
            : patientModel.Addresses[0];

        const healthCard = patientModel.HealthCards.firstOrDefault(p => p.ProvinceLookupId === cacheStore.FindLookupByLookupCode("CanadaState_QC").LookupID);
        let requestData = {
            FirstName: patientModel.IndividualResponse.FirstName,
            LastName: patientModel.IndividualResponse.LastName,
            NAM: healthCard?.Number,
            NamExpiry: healthCard?.ExpiryDate,
            DOB: patientModel.IndividualResponse.DateOfBirth,

            GenderLookupId: patientModel.IndividualResponse.GenderLookupId,

            Language: patientModel.IndividualResponse.PreferredOfficialLanguageLookupId ? cacheStore.GetLookupById(patientModel.IndividualResponse.PreferredOfficialLanguageLookupId).LookupCode : "",

            FileNumber: patientModel.IndividualResponse.Identifier01 || patientModel.IndividualResponse.FileNumber,
            StreetAddress: preferredAddress ? preferredAddress.StreetAddress : "",
            City: preferredAddress ? preferredAddress.City : "",
            ZipCode: preferredAddress ? preferredAddress.ZipCode : "",
            Country: preferredAddress ? cacheStore.GetLocalizedValueByLookupId(preferredAddress.CountryLookupId) : "",
            State: preferredAddress ? cacheStore.GetLocalizedValueByLookupId(preferredAddress.StateLookupId) : "",

            HomePhone: "",
            WorkPhone: "",
            WorkPhoneExtension: "",

            IsEmptyForm: isEmptyForm,
            PatientContactId: patientModel.IndividualResponse.Contact_id
        };

        if (patientModel.Phones) {
            patientModel.Phones.map((value: PhoneNumber) => {
                if (cacheStore.GetLocalizedValueByLookupId(value.ContactPointTypeLookupId) === "Work" || cacheStore.GetLocalizedValueByLookupId(value.ContactPointTypeLookupId) === "Travail" || cacheStore.GetLocalizedValueByLookupId(value.ContactPointTypeLookupId) === "Trabajo") {
                    requestData.WorkPhone = value.Number;
                    if (value.Extension)
                        requestData.WorkPhoneExtension = value.Extension;
                }
                if (cacheStore.GetLocalizedValueByLookupId(value.ContactPointTypeLookupId) === "Personal" || cacheStore.GetLocalizedValueByLookupId(value.ContactPointTypeLookupId) === "Personnel")
                    requestData.HomePhone = value.Number;
            });
        }

        this.Execute(ServerOperations.Server.Myle_Patient_Enrollment_GenerateRAMQForm, requestData);
    }

    // -------------------------------------------------------------------------------------------------------------------------
    // - labels and printing ----------------------------------------------------------------------------------------------------
    // -------------------------------------------------------------------------------------------------------------------------
    public printSummary(patientId: string, store: PatientStore): void {
        let patientModel = store.PatientModels[patientId];

        let requestData = {
            PatientContactId: patientModel.IndividualResponse.Contact_id,
            PatientContactFacetId: patientModel.PatientResponse.ContactFacet_id,
            PrintDiagnosisCode: settingsStore.configuration.get("ShowDiagnosisCode")
        };

        this.Execute(ServerOperations.Server.Myle_Patient_CPP_GeneratePDF, requestData);
    }

    public deactivatePatient(contactId: string, reasonId: string, note: string): void {
        if (contactId && contactId.length > 0 && reasonId && reasonId.length > 0) {
            let requestData = {
                PatientContactId: contactId,
                ReasonForInactivityLookupId: reasonId,
                DeactivationNote: note
            };

            this.Execute(ServerOperations.Server.Myle_Patient_Deactivate, requestData);
        }
    }

    public reactivatePatient(contactId: string): void {
        if (contactId && contactId.length > 0) {
            let requestData = {
                PatientContactId: contactId
            };

            this.Execute(ServerOperations.Server.Myle_Patient_Reactivate, requestData);
        }
    }

    public markTestPatient(contactId: string, isTest: boolean): void {
        if (contactId && contactId.length > 0) {
            let requestData = {
                PatientContactId: contactId,
                IsTest: isTest
            };

            this.Execute(ServerOperations.Server.Myle_Patient_MarkTestStatus, requestData);
        }
    }

    public loadConsentProgram(): void {
        this.Execute(ServerOperations.Server.Myle_LoadConsentPrograms, null, SubServerOperations.Profile.LoadForAdd);
    }

    public loadConsentProgramForEdit(): void {
        this.Execute(ServerOperations.Server.Myle_LoadConsentPrograms, null, SubServerOperations.Profile.LoadForEdit);
    }

    public addConsentProgram(model: IPatientOtherInformationModel, patientId: string): void {
        let requestData = {
            ConsentProgramId: model.get("SelectedConsentProgramId"),
            PatientContactId: patientId,
            DocGiven: model.get("SelectedConsentProgramDocGiven"),
            DocGivenDate: model.get("DocGivenDate"),
            HasConsentedOrRefused: null,
            HasConsentedOrRefusedOrDidntRespondDate: model.get("HasConsentedOrRefusedOrDidntRespondDate"),
            HasRevoked: model.get("SelectedConsentProgramHasRevoked"),
            RevokedDate: model.get("HasRevokedDate")
        };

        if (model.get("SelectedConsentProgramHasConsented") === true)
            requestData.HasConsentedOrRefused = true;
        else if (model.get("SelectedConsentProgramHasRefused") === true)
            requestData.HasConsentedOrRefused = false;

        this.Execute(ServerOperations.Server.Myle_Patient_AddConsentProgramHistory, requestData);
    }

    public loadConsentProgramHistory(patientId: string): void {
        this.Execute(ServerOperations.Server.Myle_Patient_LoadConsentProgramHistory, { PatientContactId: patientId });
    }

    public updateConsentProgram(model: IPatientOtherInformationModel, patientId: string): void {
        let requestData = {
            ConsentProgramId: model.get("SelectedConsentProgramId"),
            PatientContactId: patientId,
            DocGiven: model.get("SelectedConsentProgramDocGiven"),
            DocGivenDate: model.get("DocGivenDate"),
            HasConsentedOrRefused: null,
            HasConsentedOrRefusedOrDidntRespondDate: model.get("HasConsentedOrRefusedOrDidntRespondDate"),
            HasRevoked: model.get("SelectedConsentProgramHasRevoked"),
            RevokedDate: model.get("HasRevokedDate"),
            ConsentId: model.get("ModifyingProgramId")
        };

        if (model.get("SelectedConsentProgramHasConsented") === true)
            requestData.HasConsentedOrRefused = true;
        else if (model.get("SelectedConsentProgramHasRefused") === true)
            requestData.HasConsentedOrRefused = false;

        this.Execute(ServerOperations.Server.Myle_Patient_UpdateConsentProgram, requestData);
    }

    public removeProfilePicEmployee(id: string): void {
        let request = {
            ProfilePicId: id
        };

        this.Execute(ServerOperations.Server.DeleteProfilePicEmployee, request);
    }

    public printFiles(files: ExportableFile[], addErrorPages: boolean = true): void {
        if (!files)
            return;

        let preloadedExportableFiles = files.map(x => x.getFetchUrlDtoAsync());
        Promise.all(preloadedExportableFiles).then(preloadedExportableFile => {
            let request = {
                fetchUrls: preloadedExportableFile.toHash(x => x.FetchUrl, x => x.OriginalExportableFile.description),
                addErrorPages: addErrorPages
            };

            this.Execute(ServerOperations.Server.Myle_PrintMultipleFetchUrlIntoSingleFile, request);
        });
    }

    public updatePatientProfileNotifications(patientContactId: string, notificationsContactPointIds: string[]): void {
        sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_UpdateNotificationsFromAppointmentSave, { patientContactId, notificationsContactPointIds });
    }
}

export { PatientProfileActions };
